import Reveal, { Eyebrow, EASE } from "@/components/Reveal";
import { motion } from "framer-motion";
import { Flag } from "lucide-react";

const PHASES = [
  {
    phase: "Phase 0",
    title: "Launch readiness",
    timing: "October 2026",
    desc: "Site and fee consistency, legal pages, mint authority verification, end-to-end purchase and delivery testing, security review of the presale flow.",
    now: true,
  },
  {
    phase: "Phase 1",
    title: "Presale and listing",
    timing: "Q4 2026",
    desc: "Presale rounds 1 to 3, airdrop snapshot and claim page, DEX listing with locked liquidity and published proofs.",
  },
  {
    phase: "Phase 2",
    title: "Merchant product",
    timing: "Q1 2027",
    desc: "Pro tier, merchant dashboard (invoices, exports), staking and fee discounts, payments API and SDK, Shopify and WooCommerce plugins.",
  },
  {
    phase: "Phase 3",
    title: "Growth",
    timing: "Q2 2027",
    desc: "Recurring payments, more stablecoins (USDT, PYUSD), merchant incentive programme, published security audit, exchange applications.",
  },
  {
    phase: "Phase 4",
    title: "Expansion",
    timing: "H2 2027",
    desc: "Point-of-sale and mobile app, partner fiat off-ramps where regulation allows, governance.",
  },
];

export default function Roadmap() {
  return (
    <section id="roadmap" className="relative py-24 sm:py-28" data-testid="roadmap-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-12">
        <Reveal className="max-w-2xl">
          <Eyebrow>roadmap</Eyebrow>
          <h2 className="mt-4 text-3xl font-bold tracking-[-0.02em] text-white sm:text-4xl">
            From presale to point-of-sale.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-mut">
            Five phases from launch readiness to global expansion — dated, specific, and shipped in order.
          </p>
        </Reveal>

        <div className="mt-14 space-y-4" data-testid="roadmap-phases">
          {PHASES.map((p, i) => (
            <Reveal key={p.phase} delay={i * 0.06}>
              <motion.div
                whileHover={{ x: 6 }}
                transition={{ duration: 0.3, ease: EASE }}
                data-testid={`roadmap-phase-${i}`}
                className={`relative flex gap-5 rounded-2xl border p-6 sm:gap-8 sm:p-7 ${
                  p.now ? "border-sgreen/40 bg-sgreen/[0.05]" : "border-line bg-card2"
                }`}
              >
                <div className="flex flex-col items-center">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-mono text-xs font-bold ${
                      p.now ? "bg-sgreen text-black" : "border border-line bg-panel text-mut"
                    }`}
                  >
                    {i}
                  </span>
                  {i < PHASES.length - 1 && <span className="mt-2 w-px flex-1 bg-line" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-lg font-semibold text-white">
                      {p.phase}: {p.title}
                    </h3>
                    <span className="shrink-0 whitespace-nowrap rounded-full border border-line bg-panel px-3 py-0.5 font-mono text-[11px] uppercase tracking-[0.08em] text-mut">
                      {p.timing}
                    </span>
                    {p.now && (
                      <span className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-sgreen/10 px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-[0.08em] text-sgreen">
                        <Flag className="h-3 w-3" />
                        now
                      </span>
                    )}
                  </div>
                  <p className="mt-3 max-w-3xl text-sm leading-relaxed text-mut">{p.desc}</p>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
